// Events types for React Native admin app
// Based on admin-web events implementation

import { ISortModel, IFilterModel } from './dataTable';

// Unified photo interface for React Native events
// Supports both new photos (uri) and existing photos (photoUrl)
export interface IEventPhoto {
  id?: number;
  uri?: string;           // For React Native (local files)
  photoUrl?: string;      // For existing photos from backend
  type?: string;          // MIME type
  name?: string;          // File name
  size?: number;          // File size in bytes
  eventId?: number;       // For existing photos
}

// Legacy interface - use IEventPhoto instead
// @deprecated Use IEventPhoto instead
export interface ICreateEventPhoto {
  file: File;
  preview?: string;
}

export interface IEventResident {
  hasRsvp: boolean;
  checkedIn: boolean;
}

export interface ILeaseCustomer {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  customerType: string;
  unitNumberSpace: string;
  statusType: string;
  EventResident?: IEventResident;
  events?: Array<{
    rsvpList?: {
      hasRsvp: boolean;
      checkedIn: boolean;
    };
  }>;
}

export interface IEvent {
  id: number;
  name: string;
  description: string;
  descriptionHtml: string;
  descriptionJson: string;
  location: string;
  address: string;
  startTime: string;
  endTime: string;
  startTimeLocal: string;
  endTimeLocal: string;
  startTimeLocalFormatted: string;
  endTimeLocalFormatted: string;
  rsvpLimit: number;
  n_rsvps: number;
  n_photos: number;
  rsvp: boolean;
  propertyId: number;
  photos: IEventPhoto[];
  rsvpList?: ILeaseCustomer[];
  publishTime?: string;
  internalNotes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ICreateEvent {
  name: string;
  description: string;
  descriptionHtml: string;
  descriptionJson: string;
  location: string;
  startTime: string;
  endTime: string;
  rsvpLimit: number;
  photos: IEventPhoto[];
  publishTime?: string;
  internalNotes?: string;
}

export interface IEventFormValues {
  name: string;
  description: any; // EditorState for rich text
  location: string;
  startTime: Date | string; // DateTime object or ISO string
  endTime: Date | string; // DateTime object or ISO string
  rsvpLimit: number;
  photos: IEventPhoto[];
  publishType: 'publishNow' | 'draft' | 'scheduled';
  publishTime?: Date | string; // DateTime object or ISO string
  internalNotes?: string;
}

export interface IEventTableRow {
  id: number;
  name: string;
  startTime: string;
  endTime: string;
  location: string;
  rsvpLimit: number;
  n_rsvps: number;
  status: 'Published' | 'Scheduled' | 'Draft' | 'Past';
  publishTime?: string;
  createdAt: string;
}

export interface IEventFilterModel extends IFilterModel {
  status?: string[];
  dateRange?: {
    start?: string;
    end?: string;
  };
  month?: number;
  year?: number;
}

export interface IEventSortModel extends ISortModel {
  field: 'name' | 'startTime' | 'endTime' | 'location' | 'rsvpLimit' | 'n_rsvps' | 'createdAt';
}

export interface IEventListResponse {
  data: IEvent[];
  count: number;
  hasMore?: boolean;
}

export interface IEventResidentResponse {
  count: number;
  rows: ILeaseCustomer[];
}

// Calendar related types
export interface ICalendarKey {
  title: string;
  color: string;
}

export interface IDayEventPairings {
  [date: string]: IEvent[];
}

// Event view types
export const EVENT_VIEW_TYPES = {
  CALENDAR: 'calendar' as const,
  LIST: 'list' as const,
} as const;

export type EventViewType = typeof EVENT_VIEW_TYPES[keyof typeof EVENT_VIEW_TYPES];

// Event status types
export const EVENT_STATUSES = {
  PUBLISHED: 'Published' as const,
  SCHEDULED: 'Scheduled' as const,
  DRAFT: 'Draft' as const,
  PAST: 'Past' as const,
} as const;

export type EventStatusType = typeof EVENT_STATUSES[keyof typeof EVENT_STATUSES];

// Event panels for details view
export const EVENT_PANELS = {
  INFO: 'Event info' as const,
  RSVPS: 'Rsvps' as const,
  ATTENDANCE: 'Attendance' as const,
} as const;

export type EventPanelType = typeof EVENT_PANELS[keyof typeof EVENT_PANELS];

// Calendar keys for event status colors
export const CALENDAR_KEYS: ICalendarKey[] = [
  {
    title: 'Published',
    color: 'rgb(134 239 172)', // green-300
  },
  {
    title: 'Scheduled',
    color: '#4672FE', // blue
  },
  {
    title: 'Draft',
    color: '#FF7629', // orange
  },
  {
    title: 'Past',
    color: '#9CA3AF', // gray-400
  },
];

// Event filter labels for resident filtering
export const EVENT_FILTER_LABELS = [
  { title: "RSVP'd", key: 'hasRsvp' },
  { title: 'Checked in', key: 'checkedIn' },
  { title: 'Not checked in', key: 'notCheckedIn' },
  { title: "Not RSVP'd", key: 'notRsvp' },
];

// Constants
export const EVENT_PAGE_SIZE = 25;
